/* eslint-disable no-restricted-imports */
import { DataTable } from '@juvare/common-data-table-ui/DataTable';
import { useDataTreeUsingState } from '@juvare/common-data-table-ui/DataTree';
import { FiltersBar, useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { LayoutRows2 } from '@juvare/common-web-ui/LayoutRows2';
import { requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { useAccountsTable } from '@juvare/contact-management/ui/features/accounts/hooks';
import { useGroupsTable } from '@juvare/contact-management/ui/features/groups/hooks';
import { OrganizationTreeWithDetails } from '@juvare/contact-management/ui/features/organizations';
import { useOrganizationSelectionTree } from '@juvare/contact-management/ui/features/organizations/hooks';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Box, Button, IconButton, Tab, Tabs, Toolbar, Typography } from '@mui/material';
import React from 'react';
import { getMessageAccountsBaseFilter } from '../../../../../../backend/features/permissions';
import { useAlertConfig, useAlertUiTranslation } from '../../../../../hooks';
import { AccountsTabComponent } from './AccountsTabComponent';
import { TabPanel } from './TabPanel';
export const SelectRecipientsComponent = (props) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const userState = useUserState();
    const { onGetAdditionalTabs } = useAlertConfig();
    const { t } = useAlertUiTranslation();
    const [tabIndex, setTabIndex] = React.useState(0);
    const [includeNonMembers, setIncludeNonMembers] = React.useState(!!((_a = props.value) === null || _a === void 0 ? void 0 : _a.includeNonMembers));
    const [useAccountsFilter, setUseAccountsFilter] = React.useState(!!((_b = props.value) === null || _b === void 0 ? void 0 : _b.accountsFilter));
    const messengerFilter = React.useMemo(() => (userState ? getMessageAccountsBaseFilter(userState, includeNonMembers) : []), [userState, includeNonMembers]);
    const [internalRecipientsData, setInternalRecipientsData] = React.useState(props.value || {});
    const accountsTable = useAccountsTable({
        rowSelectionEnabled: !useAccountsFilter,
        initialSelectedRowIds: (_c = props.value) === null || _c === void 0 ? void 0 : _c.accounts,
        staticStorageClientFilter: messengerFilter,
        initialStorageClientFilter: (_d = props.value) === null || _d === void 0 ? void 0 : _d.accountsFilter,
        showAccountSystemRoleFilter: 'onlyAllFiltersPanel',
    });
    const groupsTable = useGroupsTable({
        rowSelectionEnabled: true,
        initialSelectedRowIds: (_e = props.value) === null || _e === void 0 ? void 0 : _e.groups,
    });
    const organizationsTree = useOrganizationSelectionTree({
        useDataTreeFunction: useDataTreeUsingState,
        initialSelectedOrganizationIds: (_g = (_f = props.value) === null || _f === void 0 ? void 0 : _f.organizations) !== null && _g !== void 0 ? _g : undefined,
        staticFilters: {
            permission: requireAnySystemPermission(userState, 'all-organizations-admin') ? undefined : 'manage',
            orgPermissions: requireAnySystemPermission(userState, 'all-organizations-admin')
                ? undefined
                : ['view-and-message-members', 'create-notifications'],
        },
    });
    const organizationTreeFilterConfig = useFilterConfig((builder) => {
        builder.quickSearchFilter({
            sx: { maxWidth: { xs: undefined, sm: 340 } },
            label: t('common.search'),
            dataPath: 'search',
            dataTest: 'search-filter',
        });
    }, [t]);
    const handleSave = () => {
        var _a, _b, _c, _d, _e;
        const uniqueOrganizationIds = [...new Set((_a = organizationsTree.selectedOrganizationIds) !== null && _a !== void 0 ? _a : [])];
        props.setValue(Object.assign(Object.assign({}, internalRecipientsData), { accounts: !useAccountsFilter ? ((_b = accountsTable.dataTableProps.selectedRowIds) !== null && _b !== void 0 ? _b : []) : [], accountsFilter: useAccountsFilter
                ? (_d = (_c = accountsTable.dataTableProps.customBackendResponseData) === null || _c === void 0 ? void 0 : _c.storageClientRequest) === null || _d === void 0 ? void 0 : _d.filter
                : undefined, includeNonMembers: includeNonMembers, groups: (_e = groupsTable.dataTableProps.selectedRowIds) !== null && _e !== void 0 ? _e : [], organizations: uniqueOrganizationIds }));
        props.handleClose();
    };
    const defaultTabs = [
        {
            name: 'organizations',
            label: t('widget.recipients.organizations'),
            render: () => (React.createElement(OrganizationTreeWithDetails, { dataTreeProps: organizationsTree.dataTreeProps, multiSelect: true, filtersBar: React.createElement(FiltersBar, Object.assign({}, organizationsTree.filtersBarProps, { config: organizationTreeFilterConfig })), activeOrganizationId: organizationsTree.activeOrganizationId })),
        },
        {
            name: 'groups',
            label: t('widget.recipients.groups'),
            render: () => (React.createElement(Box, { sx: { height: '100%', boxSizing: 'border-box', p: 2, overflow: 'auto' } },
                React.createElement(DataTable, Object.assign({}, groupsTable.dataTableProps, { "data-test": 'groups-table' })))),
        },
        {
            name: 'accounts',
            label: t('widget.recipients.accounts'),
            render: () => {
                return (React.createElement(AccountsTabComponent, { accountsTable: accountsTable, includeNonMembers: includeNonMembers, onAccountsFilterChange: setUseAccountsFilter, onIncludeNonMembersChange: setIncludeNonMembers, useAccountsFilter: useAccountsFilter }));
            },
        },
    ];
    const tabs = onGetAdditionalTabs ? onGetAdditionalTabs(defaultTabs) : defaultTabs;
    return (React.createElement(LayoutRows2, { row1: React.createElement(React.Fragment, null,
            React.createElement(AppBar, { sx: { position: 'static' } },
                React.createElement(Toolbar, null,
                    React.createElement(IconButton, { edge: 'start', color: 'inherit', onClick: () => props.handleClose(), "aria-label": t('widget.recipients.closeRecipientsSelector') },
                        React.createElement(CloseIcon, null)),
                    React.createElement(Typography, { id: props.ariaLabelledBy, variant: 'h6', sx: { flexGrow: 1 } }, t('widget.recipients.addRecipients')),
                    React.createElement(Button, { color: 'inherit', onClick: handleSave }, t('widget.recipients.save')))),
            React.createElement(Tabs, { sx: (theme) => ({ mt: 2, borderBottom: '1px solid ' + theme.palette.divider }), value: tabIndex, onChange: (e, newValue) => setTabIndex(newValue) }, tabs.map((tab) => (React.createElement(Tab, { key: tab.name, label: tab.label, "data-test": tab.name + '-tab' }))))), row2: tabs.map((tab, i) => (React.createElement(TabPanel, { value: tabIndex, key: tab.name + '-tab-content', index: i }, tab.render(internalRecipientsData, {
            onChange: (recipients) => {
                setInternalRecipientsData((prevState) => {
                    return Object.assign(Object.assign({}, prevState), recipients);
                });
            },
        })))), fullHeightRow: 'row2' }));
};
